import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { interlude } from "@/interlude";
import { useValidateUrl } from "@/pages/applications/pages/productFeeds/hooks/useValidateUrl";

export const useUrlFeedForm = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [feedName, setFeedName] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [syncFrequency, setSyncFrequency] = useState<"daily">("daily");

  const validateUrl = useValidateUrl();
  const createFeedMutation = interlude.productFeed.useCreateFeed();

  const handleSubmit = useHandler(async () => {
    if (!feedName.trim()) {
      enqueueSnackbar(t("productFeed.urlFeed.error.nameRequired"), {
        variant: "error",
      });
      return;
    }

    if (!feedUrl.trim()) {
      enqueueSnackbar(t("productFeed.urlFeed.error.urlRequired"), {
        variant: "error",
      });
      return;
    }

    if (!validateUrl(feedUrl.trim())) {
      return;
    }

    try {
      await createFeedMutation.mutateAsync({
        name: feedName.trim(),
        feedUrl: feedUrl.trim(),
        sourceType: "feed_url",
        filename: null,
      });

      // Success: Navigate to list page
      navigate({ to: "/applications/product-feeds" });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      enqueueSnackbar(
        `${t("productFeed.urlFeed.error.createFailed")}: ${errorMessage}`,
        {
          variant: "error",
        },
      );
    }
  });

  return {
    feedName,
    setFeedName,
    feedUrl,
    setFeedUrl,
    syncFrequency,
    setSyncFrequency,
    isSubmitting: createFeedMutation.isPending,
    handleSubmit,
  };
};
